import type { WebsiteConfig } from "../runtime/types/core";
import type {
  BodyTheme,
  ButtonsTheme,
  FormInputTheme,
  HeadingsTheme,
  HyperlinkTheme,
  ResolvedTheme,
} from "../runtime/types/theme";

export const DEFAULT_BODY_THEME: BodyTheme = {
  fontColor: "#7B7B7B",
  fontSize: "16px",
  layoutOption: "full-width",
};

export const DEFAULT_HEADINGS_THEME: HeadingsTheme = {
  h1: {
    fontFamily: "",
    fontSize: "36px",
    fontColor: "#000000",
  },
  h2: {
    fontFamily: "",
    fontSize: "22px",
    fontColor: "#000000",
  },
};

export const DEFAULT_BUTTONS_THEME: ButtonsTheme = {
  primary: {
    bgColor: "#000000",
    bgHoverColor: "#000000",
    borderColor: "#000000",
    borderHoverColor: "#000000",
    fontColor: "#FFFFFF",
    fontHoverColor: "#FFFFFF",
    fontSize: "16px",
    fontHoverSize: "16px",
    borderRadius: "rounded-lg",
    hoverBorderRadius: "rounded-lg",
  },
  secondary: {
    bgColor: "transparent",
    bgHoverColor: "transparent",
    borderColor: "#7B7B7B",
    borderHoverColor: "#7B7B7B",
    fontColor: "#7B7B7B",
    fontHoverColor: "#7B7B7B",
    fontSize: "16px",
    fontHoverSize: "16px",
    borderRadius: "rounded-lg",
    hoverBorderRadius: "rounded-lg",
  },
};

export const DEFAULT_HYPERLINK_THEME: HyperlinkTheme = {
  fontColor: "inherit",
  fontHoverColor: "#0066cc",
  underline: false,
};

export const DEFAULT_FORM_INPUT_THEME: FormInputTheme = {
  labelFontColor: "#7B7B7B",
  inputFontColor: "#7B7B7B",
  backgroundColor: "#FFFFFF",
  border: true,
  borderColor: "#7B7B7B",
  borderRadius: "rounded-md",
  borderStyle: "border-solid",
  borderWidth: "border",
  borderSides: [],
  fontFamily: "Inter, system-ui, sans-serif",
  fontSize: "text-sm",
  errorColor: "#ef4444",
};

function unwrapMaybeQuoted(value: string): string {
  const trimmed = value.trim();
  if (
    (trimmed.startsWith('"') && trimmed.endsWith('"'))
    || (trimmed.startsWith("'") && trimmed.endsWith("'"))
  ) {
    return trimmed.slice(1, -1).trim();
  }
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (typeof parsed === "string") return parsed.trim();
  } catch {
    // Keep original if not JSON string.
  }
  return trimmed;
}

function nonEmpty(value: string | undefined | null): string | undefined {
  if (typeof value !== "string") return undefined;
  const unwrapped = unwrapMaybeQuoted(value);
  return unwrapped.length > 0 ? unwrapped : undefined;
}

function coalesceString(
  ...values: Array<string | undefined | null>
): string | undefined {
  for (const value of values) {
    const normalized = nonEmpty(value);
    if (normalized !== undefined) return normalized;
  }
  return undefined;
}

function coalesceStringArray(value: unknown, fallback: string[]): string[] {
  if (Array.isArray(value)) {
    const normalized = value
      .filter((item): item is string => typeof item === "string")
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
    return normalized.length > 0 ? normalized : fallback;
  }
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value) as unknown;
      if (Array.isArray(parsed)) {
        const normalized = parsed
          .filter((item): item is string => typeof item === "string")
          .map((item) => item.trim())
          .filter((item) => item.length > 0);
        return normalized.length > 0 ? normalized : fallback;
      }
    } catch {
      // Not a JSON array string.
    }
  }
  return fallback;
}

export function resolveThemeFromWebsiteConfig(mp: WebsiteConfig): ResolvedTheme {
  return {
    bodyTheme: {
      fontColor: coalesceString(mp.bodyFontColor),
      fontSize: coalesceString(mp.bodyFontSize),
      layoutOption:
        coalesceString(mp.bodyLayoutOption, DEFAULT_BODY_THEME.layoutOption)
        ?? DEFAULT_BODY_THEME.layoutOption,
    },
    headingsTheme: {
      h1: {
        fontFamily: coalesceString(mp.h1FontFamily),
        fontSize: coalesceString(mp.h1FontSize),
        fontColor: coalesceString(mp.h1FontColor),
      },
      h2: {
        fontFamily: coalesceString(mp.h2FontFamily),
        fontSize: coalesceString(mp.h2FontSize),
        fontColor: coalesceString(mp.h2FontColor),
      },
    },
    buttonsTheme: {
      primary: {
        bgColor: coalesceString(mp.primaryBtnBgColor),
        bgHoverColor: coalesceString(mp.primaryBtnBgHoverColor),
        borderColor: coalesceString(mp.primaryBtnBorderColor),
        borderHoverColor: coalesceString(mp.primaryBtnBorderHoverColor),
        fontColor: coalesceString(mp.primaryBtnFontColor),
        fontHoverColor: coalesceString(mp.primaryBtnFontHoverColor),
        fontSize: coalesceString(mp.primaryBtnFontSize),
        fontHoverSize: coalesceString(mp.primaryBtnFontHoverSize),
        borderRadius: coalesceString(mp.primaryBtnBorderRadius),
        hoverBorderRadius: coalesceString(mp.primaryBtnHoverBorderRadius),
      },
      secondary: {
        bgColor: coalesceString(mp.secondaryBtnBgColor),
        bgHoverColor: coalesceString(mp.secondaryBtnBgHoverColor),
        borderColor: coalesceString(mp.secondaryBtnBorderColor),
        borderHoverColor: coalesceString(mp.secondaryBtnBorderHoverColor),
        fontColor: coalesceString(mp.secondaryBtnFontColor),
        fontHoverColor: coalesceString(mp.secondaryBtnFontHoverColor),
        fontSize: coalesceString(mp.secondaryBtnFontSize),
        fontHoverSize: coalesceString(mp.secondaryBtnFontHoverSize),
        borderRadius: coalesceString(mp.secondaryBtnBorderRadius),
        hoverBorderRadius: coalesceString(mp.secondaryBtnHoverBorderRadius),
      },
    },
    hyperlinkTheme: {
      fontColor: coalesceString(mp.hyperlinkFontColor),
      fontHoverColor: coalesceString(mp.hyperlinkFontHoverColor),
      underline: mp.hyperlinkUnderline,
    },
    formInputTheme: {
      labelFontColor: coalesceString(mp.formInputLabelFontColor),
      inputFontColor: coalesceString(mp.formInputInputFontColor),
      backgroundColor: coalesceString(mp.formInputBackgroundColor),
      border: mp.formInputBorder,
      borderColor: coalesceString(mp.formInputBorderColor),
      borderRadius: coalesceString(mp.formInputBorderRadius),
      borderStyle: coalesceString(mp.formInputBorderStyle),
      borderWidth: coalesceString(mp.formInputBorderWidth),
      borderSides:
        coalesceStringArray(
          mp.formInputBorderSides,
          DEFAULT_FORM_INPUT_THEME.borderSides ?? [],
        ),
      fontFamily: coalesceString(mp.formInputFontFamily),
      fontSize: coalesceString(mp.formInputFontSize),
      errorColor: coalesceString(mp.formInputErrorColor),
    },
    customCss: coalesceString(mp.customCss, "") ?? "",
  };
}
